import { Check } from 'lucide-react';

import type { ProposedActionPayloadT } from '@lotushealth/lotushealth_js_sdk/schemas';
import { cn } from '@/lib/utils';

function summarizeValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value == null) return 'null';
  if (Array.isArray(value)) return `[${value.length} item${value.length === 1 ? '' : 's'}]`;
  if (typeof value === 'object') return '{...}';
  return String(value);
}

function payloadEntries(payload: ProposedActionPayloadT): Array<[string, string]> {
  return Object.entries(payload.tool_input ?? {})
    .slice(0, 6)
    .map(([key, value]) => [key, summarizeValue(value)]);
}

interface PayloadOptionCardProps {
  payload: ProposedActionPayloadT;
  isSelected: boolean;
  onSelect: (() => void) | undefined;
}

export function PayloadOptionCard({ payload, isSelected, onSelect }: PayloadOptionCardProps) {
  const entries = payloadEntries(payload);

  return (
    <button
      type="button"
      disabled={isSelected || !onSelect}
      onClick={onSelect}
      className={cn('w-[280px] shrink-0 text-left', (isSelected || !onSelect) && 'cursor-default')}
    >
      <div
        className={cn(
          'rounded-xl border px-4 py-3 transition-all duration-200',
          isSelected
            ? 'border-primary bg-primary/[0.03] shadow-[0_0_0_1px] shadow-primary/20'
            : 'border-border/50 bg-card hover:border-primary/30 hover:shadow-sm'
        )}
      >
        <p className="text-[13px] font-semibold leading-snug text-foreground">
          {payload.justification || 'No justification provided.'}
        </p>

        <div className="mt-3 space-y-2.5">
          {entries.map(([key, value]) => (
            <div key={key} className="flex min-w-0 flex-col gap-0.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
                {key}
              </span>
              <span className="whitespace-normal break-words text-[12px] font-medium text-foreground">
                {value}
              </span>
            </div>
          ))}
        </div>

        {isSelected ? (
          <div className="mt-2.5 flex items-center gap-1.5 text-[11px] font-semibold text-primary">
            <Check className="size-3" strokeWidth={3} />
            Selected
          </div>
        ) : null}
      </div>
    </button>
  );
}
